import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetInvitationQueryResponse, GetInvitationPathParams, GetInvitation404 } from "../types/GetInvitation";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetInvitationClient = typeof client<GetInvitationQueryResponse, GetInvitation404, never>;
type GetInvitation = {
    data: GetInvitationQueryResponse;
    error: GetInvitation404;
    request: never;
    pathParams: GetInvitationPathParams;
    queryParams: never;
    headerParams: never;
    response: GetInvitationQueryResponse;
    client: {
        parameters: Partial<Parameters<GetInvitationClient>[0]>;
        return: Awaited<ReturnType<GetInvitationClient>>;
    };
};
export const getInvitationQueryKey = (inviteToken: GetInvitationPathParams["inviteToken"]) => [{ url: "/api/invitation/:inviteToken", params: { inviteToken: inviteToken } }] as const;
export type GetInvitationQueryKey = ReturnType<typeof getInvitationQueryKey>;
export function getInvitationQueryOptions(inviteToken: GetInvitationPathParams["inviteToken"], options: GetInvitation["client"]["parameters"] = {}) {
    const queryKey = getInvitationQueryKey(inviteToken);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetInvitation["data"], GetInvitation["error"]>({
                method: "get",
                url: `/api/invitation/${inviteToken}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @summary Get
 * @link /api/invitation/:inviteToken
 */
export function useGetInvitation<TData = GetInvitation["response"], TQueryData = GetInvitation["response"], TQueryKey extends QueryKey = GetInvitationQueryKey>(inviteToken: GetInvitationPathParams["inviteToken"], options: {
    query?: Partial<QueryObserverOptions<GetInvitation["response"], GetInvitation["error"], TData, TQueryData, TQueryKey>>;
    client?: GetInvitation["client"]["parameters"];
} = {}): UseQueryResult<TData, GetInvitation["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getInvitationQueryKey(inviteToken);
    const query = useQuery({
        ...getInvitationQueryOptions(inviteToken, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetInvitation["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getInvitationSuspenseQueryKey = (inviteToken: GetInvitationPathParams["inviteToken"]) => [{ url: "/api/invitation/:inviteToken", params: { inviteToken: inviteToken } }] as const;
export type GetInvitationSuspenseQueryKey = ReturnType<typeof getInvitationSuspenseQueryKey>;
export function getInvitationSuspenseQueryOptions(inviteToken: GetInvitationPathParams["inviteToken"], options: GetInvitation["client"]["parameters"] = {}) {
    const queryKey = getInvitationSuspenseQueryKey(inviteToken);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetInvitation["data"], GetInvitation["error"]>({
                method: "get",
                url: `/api/invitation/${inviteToken}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @summary Get
 * @link /api/invitation/:inviteToken
 */
export function useGetInvitationSuspense<TData = GetInvitation["response"], TQueryKey extends QueryKey = GetInvitationSuspenseQueryKey>(inviteToken: GetInvitationPathParams["inviteToken"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetInvitation["response"], GetInvitation["error"], TData, TQueryKey>>;
    client?: GetInvitation["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetInvitation["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getInvitationSuspenseQueryKey(inviteToken);
    const query = useSuspenseQuery({
        ...getInvitationSuspenseQueryOptions(inviteToken, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetInvitation["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}